import { useEffect, useState } from "react";
import { useParams } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Trash2,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { useProject, projectKey, useDeleteFile } from "@/hooks/useProjects";
import { useUpdateFileTag } from "@/hooks/useFiles";
import { useAiJob } from "@/hooks/useAiJob";
import {
  api,
  type RFPSummary as RFPSummaryShape,
  type FileTag,
  type ProjectFile,
  type ProjectDetail,
} from "@/lib/api";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { TagChip } from "@/components/TagChip";
import NextStepFooter from "@/components/NextStepFooter";
import NextStepCard from "@/components/NextStepCard";
import DeadlineBanner from "@/components/DeadlineBanner";
import LifecycleCard from "@/components/LifecycleCard";
import AiBrainPanel from "@/components/AiBrainPanel";
import { useOrgContext } from "@/hooks/useOrgContext";

export default function RFPSummaryPage() {
  const params = useParams<{ id?: string }>();
  const projectId = params.id;
  const { data: project, isLoading } = useProject(projectId ?? null);
  const { data: orgContextData } = useOrgContext();
  const qc = useQueryClient();

  const [jobId, setJobId] = useState<string | null>(null);
  const job = useAiJob<RFPSummaryShape>(jobId);
  const [removeFileOpen, setRemoveFileOpen] = useState(false);
  const [removeFileId, setRemoveFileId] = useState<string | null>(null);
  const [autoTried, setAutoTried] = useState(false);
  const deleteFile = useDeleteFile(projectId ?? "");
  const updateFileTag = useUpdateFileTag(projectId ?? "");

  // When the job finishes, refetch the project so the saved summary appears.
  useEffect(() => {
    if (job.data?.status === "complete" && projectId) {
      qc.invalidateQueries({ queryKey: projectKey(projectId) });
    }
  }, [job.data?.status, projectId, qc]);

  const launch = useMutation({
    mutationFn: () => {
      if (!projectId) throw new Error("No project id");
      return api.parseSummary(projectId);
    },
    onSuccess({ jobId: id }) {
      setJobId(id);
    },
  });

  // Auto-recover: if the project has source text but no summary and no job
  // is currently running, kick off parse-summary once. This handles the case
  // where the auto-summarize step after upload failed or never fired.
  useEffect(() => {
    if (!project) return;
    if (project.summary) return;
    if (!project.rawRfpText) return; // wait for parse_file to finish
    if (autoTried) return;
    if (launch.isPending) return;
    if (job.data?.status === "queued" || job.data?.status === "running") return;
    setAutoTried(true);
    launch.mutate();
  }, [project, autoTried, launch, job.data?.status]);

  const runStatus = job.data?.status;
  const running = runStatus === "queued" || runStatus === "running" || launch.isPending;

  if (isLoading || !project) {
    return (
      <Layout>
        <PageHeader title="RFP Summary" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  const summary = project.summary ?? null;

  const handleRemoveFile = async () => {
    if (!projectId || !removeFileId) return;
    try {
      await deleteFile.mutateAsync(removeFileId);
      toast.success("File removed.");
      setRemoveFileOpen(false);
      setRemoveFileId(null);
    } catch (err) {
      toast.error((err as Error).message ?? "Couldn't remove the file.");
    }
  };

  return (
    <Layout projectTitle={project.title} projectId={projectId}>
      <DeadlineBanner project={project} />
      <PageHeader
        title="Summary"
        subtitle="The key details from the RFP, pulled out automatically."
        actions={
          <Button
            variant="primary"
            onClick={() => launch.mutate()}
            disabled={running || !project.rawRfpText}
            icon={running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          >
            {running ? "Reading RFP…" : summary ? "Read again" : "Read RFP"}
          </Button>
        }
      />

      <div className="p-8 pb-24 space-y-4">
        {/* "What should I do next on this bid?" — state-aware guidance. */}
        {projectId && <NextStepCard project={project} projectId={projectId} />}

        {/* Lifecycle close — Submit / Won / Lost actions. */}
        {projectId && <LifecycleCard project={project} projectId={projectId} />}

        {/* AI confidence panel — collapsed by default for return visits. */}
        {projectId && (
          <AiBrainPanel
            project={project}
            projectId={projectId}
            orgContext={orgContextData?.context}
          />
        )}

        {/* Loading / status cards (extraction in progress, error, etc.) */}
        {!project.rawRfpText && project.rfpFileId && (
          <Card>
            <div className="flex items-center gap-3">
              <Loader2 size={16} className="animate-spin text-orange-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Reading the file you uploaded…</p>
                <p className="mt-0.5 text-xs" style={{ color: "#6B7280" }}>
                  This typically takes 1–3 minutes. The page will update on its own when it's done.
                </p>
                <div
                  className="mt-3 h-1.5 w-full rounded-full overflow-hidden"
                  style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
                >
                  <div
                    className="h-full rounded-full animate-pulse"
                    style={{ width: "60%", backgroundColor: "#F97316" }}
                  />
                </div>
              </div>
            </div>
          </Card>
        )}

        {!project.rawRfpText && !project.rfpFileId && (
          <Card>
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="text-yellow-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-white">No RFP text on file.</p>
                <p className="mt-1 text-xs" style={{ color: "#6B7280" }}>
                  Paste or upload the RFP source on the New RFP screen first.
                </p>
              </div>
            </div>
          </Card>
        )}

        {runStatus === "failed" && job.data?.error && (
          <Card>
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">Couldn't read the RFP</p>
                <p className="mt-1 text-xs" style={{ color: "#6B7280" }}>
                  {job.data.error}
                </p>
              </div>
            </div>
          </Card>
        )}

        {running && (
          <Card>
            <div className="flex items-center gap-3">
              <Loader2 size={16} className="animate-spin text-orange-400" />
              <div>
                <p className="text-sm font-medium text-white">
                  {runStatus === "running" ? "Reading the RFP…" : "Getting started…"}
                </p>
                <p className="mt-0.5 text-xs" style={{ color: "#6B7280" }}>
                  This typically takes 1–3 minutes.
                </p>
              </div>
            </div>
          </Card>
        )}

        {summary ? (
          <SummaryView project={project} summary={summary} />
        ) : (
          !running &&
          (project.rawRfpText ? (
            <Card>
              <div className="flex items-start gap-3">
                <Sparkles size={16} style={{ color: "#F97316" }} className="mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">Ready to read the RFP.</p>
                  <p className="mt-1 text-xs" style={{ color: "#6B7280" }}>
                    {autoTried
                      ? "We tried once and it didn't go through. Click Read RFP to try again."
                      : "Click Read RFP at the top right to pull out the key details."}
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="flex items-start gap-3">
                <Sparkles size={16} style={{ color: "#F97316" }} className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">Nothing to read yet.</p>
                  <p className="mt-1 text-xs" style={{ color: "#6B7280" }}>
                    Upload an RFP file or paste the text on the New RFP screen first.
                  </p>
                </div>
              </div>
            </Card>
          ))
        )}

        {/* Source files — collapsed by default to reduce noise. */}
        {project.files && project.files.length > 0 && projectId && (
          <FilesSection
            files={project.files}
            projectId={projectId}
            onTagChange={(fileId, tag) => updateFileTag.mutate({ fileId, tag })}
            onDelete={(fileId) => {
              setRemoveFileId(fileId);
              setRemoveFileOpen(true);
            }}
            isUpdating={updateFileTag.isPending}
          />
        )}
      </div>

      <ConfirmDeleteDialog
        open={removeFileOpen}
        onOpenChange={setRemoveFileOpen}
        title="Remove the file?"
        description="This deletes the uploaded file. If this is a main scope file, the Summary will need to be re-read. The matrix, drafts, and other work on this project are kept."
        requiredPhrase="remove"
        confirmLabel="Remove file"
        onConfirm={handleRemoveFile}
        busy={deleteFile.isPending}
      />

      {projectId && <NextStepFooter currentStep="summary" projectId={projectId} />}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// SummaryView — the structured RFP brief once the AI has parsed it.
// ---------------------------------------------------------------------------

function SummaryView({ project, summary }: { project: ProjectDetail; summary: RFPSummaryShape }) {
  return (
    <div className="space-y-4">
      <HeroStrip project={project} summary={summary} />
      <ScopeBlock scope={summary.scope} />
      <CollapsibleSection title="Deliverables" count={(summary.deliverables ?? []).length}>
        <DeliverablesGrouped items={summary.deliverables ?? []} />
      </CollapsibleSection>
      <CollapsibleSection title="Key requirements" count={(summary.keyRequirements ?? []).length}>
        <BulletList items={summary.keyRequirements ?? []} />
      </CollapsibleSection>
      <CollapsibleSection
        title="Evaluation criteria"
        count={(summary.evaluationCriteria ?? []).length}
      >
        <BulletList items={summary.evaluationCriteria ?? []} />
      </CollapsibleSection>
      <CollapsibleSection title="Required documents" count={(summary.requiredDocs ?? []).length}>
        <BulletList items={summary.requiredDocs ?? []} />
      </CollapsibleSection>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroStrip — at-a-glance row of stats. Deadline shows date + days-left from
// the project's structured submissionDeadline; falls back to RFP-extracted
// text when no structured date was set.
// ---------------------------------------------------------------------------

function HeroStrip({ project, summary }: { project: ProjectDetail; summary: RFPSummaryShape }) {
  const deadlineDate = project.submissionDeadline ? new Date(project.submissionDeadline) : null;
  const daysLeft = deadlineDate
    ? Math.ceil((deadlineDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  let deadlineValue: string;
  let deadlineSub: string | undefined;
  let accent: "danger" | "warn" | undefined;
  if (deadlineDate && daysLeft !== null) {
    deadlineValue = deadlineDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    if (daysLeft < 0) {
      deadlineSub = `${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? "" : "s"} overdue`;
      accent = "danger";
    } else if (daysLeft === 0) {
      deadlineSub = "due today";
      accent = "danger";
    } else if (daysLeft < 14) {
      deadlineSub = `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
      accent = "warn";
    } else {
      deadlineSub = `${daysLeft} days left`;
    }
  } else {
    deadlineValue = summary.deadline ? truncate(summary.deadline, 32) : "Not set";
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <StatCard label="Deadline" value={deadlineValue} sub={deadlineSub} accent={accent} />
      <StatCard
        label="Submission"
        value={truncate(summary.submissionFormat || "Not specified", 32)}
      />
      <StatCard label="Budget" value={summary.budget || "Not stated"} />
      <StatCard label="Deliverables" value={`${(summary.deliverables ?? []).length}`} />
      <StatCard label="Required docs" value={`${(summary.requiredDocs ?? []).length}`} />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: "danger" | "warn";
}) {
  const valueColor = accent === "danger" ? "#ef4444" : accent === "warn" ? "#eab308" : "#FFFFFF";
  const subColor = accent === "danger" ? "#ef4444" : accent === "warn" ? "#eab308" : "#6B7280";
  return (
    <div
      className="rounded-xl border px-4 py-3"
      style={{
        backgroundColor: "#1F2933",
        borderColor: "rgba(229,231,235,0.08)",
      }}
    >
      <div
        className="text-[10px] uppercase font-semibold mb-1"
        style={{ color: "#6B7280", letterSpacing: "0.05em" }}
      >
        {label}
      </div>
      <div className="text-sm font-bold leading-tight" style={{ color: valueColor }}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] mt-1" style={{ color: subColor }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
}

// ---------------------------------------------------------------------------
// ScopeBlock — the project's purpose paragraph as a hero block.
// ---------------------------------------------------------------------------

function ScopeBlock({ scope }: { scope: string }) {
  if (!scope || scope === "—") return null;
  return (
    <Card>
      <div
        className="text-[10px] uppercase font-semibold mb-2"
        style={{ color: "#6B7280", letterSpacing: "0.05em" }}
      >
        Scope
      </div>
      <p className="text-sm text-white leading-relaxed">{scope}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// CollapsibleSection — title + count badge, expand on click.
// ---------------------------------------------------------------------------

function CollapsibleSection({
  title,
  count,
  defaultOpen = false,
  children,
}: {
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: "rgba(249,115,22,0.15)",
              color: "#F97316",
            }}
          >
            {count}
          </span>
        </div>
        <ChevronDown
          size={16}
          className="text-gray-400 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
      {open && <div className="mt-4">{children}</div>}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// BulletList — used for plain lists (Key Requirements, Evaluation, Docs).
// ---------------------------------------------------------------------------

function BulletList({ items }: { items: string[] }) {
  if (items.length === 0) {
    return (
      <p className="text-sm" style={{ color: "#6B7280" }}>
        Nothing extracted.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-white">
          <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: "#F97316" }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// DeliverablesGrouped — auto-detects "PART N — NAME:" prefixes and groups
// items under sub-headers. Falls back to a flat list when no PART pattern
// is found.
// ---------------------------------------------------------------------------

interface DeliverableGroup {
  groupName: string | null;
  items: string[];
}

function groupDeliverables(items: string[]): DeliverableGroup[] {
  const groups: DeliverableGroup[] = [];
  // Capture: "PART 1 — TRADITIONAL MEDIA: ..." (em-dash, en-dash, or hyphen)
  const partRe = /^PART\s+(\d+)\s*[—–-]\s*([^:]+):\s*(.*)$/i;

  for (const item of items) {
    const match = item.match(partRe);
    if (match) {
      const groupName = `Part ${match[1]} — ${match[2].trim()}`;
      const itemContent = match[3].trim() || item;
      let group = groups.find((g) => g.groupName === groupName);
      if (!group) {
        group = { groupName, items: [] };
        groups.push(group);
      }
      group.items.push(itemContent);
    } else {
      let other = groups.find((g) => g.groupName === "Other");
      if (!other) {
        other = { groupName: "Other", items: [] };
        groups.push(other);
      }
      other.items.push(item);
    }
  }

  // If nothing matched (just one "Other" group), return flat (no headers).
  if (groups.length === 1 && groups[0]?.groupName === "Other") {
    return [{ groupName: null, items: groups[0].items }];
  }

  return groups;
}

function DeliverablesGrouped({ items }: { items: string[] }) {
  if (items.length === 0) {
    return (
      <p className="text-sm" style={{ color: "#6B7280" }}>
        Nothing extracted.
      </p>
    );
  }

  const groups = groupDeliverables(items);

  return (
    <div className="space-y-5">
      {groups.map((group, gi) => (
        <div key={gi}>
          {group.groupName && (
            <p
              className="text-[10px] uppercase font-semibold mb-2"
              style={{ color: "#F97316", letterSpacing: "0.05em" }}
            >
              {group.groupName}
            </p>
          )}
          <ul className="space-y-2">
            {group.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white">
                <CheckCircle2
                  size={14}
                  className="mt-0.5 flex-shrink-0"
                  style={{ color: "#F97316" }}
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilesSection — collapsible. Header line summarises count + main-scope split.
// ---------------------------------------------------------------------------

function FilesSection({
  files,
  projectId,
  onTagChange,
  onDelete,
  isUpdating,
}: {
  files: ProjectFile[];
  projectId: string;
  onTagChange: (fileId: string, tag: FileTag) => void;
  onDelete: (fileId: string) => void;
  isUpdating: boolean;
}) {
  const [open, setOpen] = useState(false);

  const mainScope = files.filter((f) => f.tag === "main_scope").length;
  const classifying = files.filter((f) => f.tag === null).length;
  const other = files.length - mainScope - classifying;

  const summaryLine = files.length === 1 ? "1 file attached" : `${files.length} files attached`;

  const detail: string[] = [];
  if (mainScope > 0) detail.push(`${mainScope} main scope`);
  if (other > 0) detail.push(`${other} other`);
  if (classifying > 0) detail.push(`${classifying} classifying…`);
  const detailLine = detail.join(" · ");

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
          >
            <FileText size={16} style={{ color: "#F97316" }} />
          </div>
          <div>
            <p className="text-sm font-medium text-white">{summaryLine}</p>
            <p className="text-xs" style={{ color: "#6B7280" }}>
              {detailLine || "—"}
            </p>
          </div>
        </div>
        <ChevronDown
          size={16}
          className="text-gray-400 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          <p className="text-xs" style={{ color: "#6B7280" }}>
            AI reads only the <strong>main scope</strong> files for the Summary. Other tags are
            stored and used by Drafts and Strategy when relevant.
          </p>

          <div className="space-y-2">
            {files.map((file) => (
              <FileRow
                key={file.id}
                file={file}
                projectId={projectId}
                onTagChange={(tag) => onTagChange(file.id, tag)}
                onDelete={(fileId) => onDelete(fileId)}
                isUpdating={isUpdating}
              />
            ))}
          </div>

          <div>
            <button
              type="button"
              disabled
              className="text-xs font-medium cursor-not-allowed"
              style={{ color: "#6B7280" }}
              title="Coming in Pass 3"
            >
              Coming soon — add files after creation
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// FileRow — individual file with tag chip + change-tag dropdown + delete.
// (Reused by FilesSection.)
// ---------------------------------------------------------------------------

interface FileRowProps {
  file: ProjectFile;
  projectId: string;
  onTagChange: (tag: FileTag) => void;
  onDelete: (fileId: string) => void;
  isUpdating: boolean;
}

function FileRow({ file, onTagChange, onDelete, isUpdating }: FileRowProps) {
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);

  const tags: FileTag[] = ["main_scope", "addendum", "qa", "evaluation", "reference"];
  const tagLabels: Record<FileTag, string> = {
    main_scope: "Main scope",
    addendum: "Addendum",
    qa: "Q&A clarifications",
    evaluation: "Evaluation criteria",
    reference: "Reference document",
  };

  const tagSourceLabels: Record<string, string> = {
    ai: "AI tagged",
    user: "You tagged",
    default: "Default tag",
  };

  return (
    <div
      className="rounded-lg border p-3"
      style={{
        backgroundColor: "rgba(255,255,255,0.02)",
        borderColor: "rgba(229,231,235,0.06)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: "rgba(249,115,22,0.08)" }}
        >
          <FileText size={14} style={{ color: "#F97316" }} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{file.filename}</p>
          <p className="text-xs mt-0.5" style={{ color: "#6B7280" }}>
            {(file.byteSize / 1024 / 1024).toFixed(1)} MB
          </p>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <TagChip tag={file.tag} size="sm" />
            <p className="text-xs" style={{ color: "#6B7280" }}>
              {file.tag && file.tagSource
                ? tagSourceLabels[file.tagSource] || "Classified"
                : "Classifying…"}
            </p>
          </div>

          <div className="relative mt-2">
            <button
              type="button"
              onClick={() => setTagDropdownOpen(!tagDropdownOpen)}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white"
              style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
            >
              Change tag
              <ChevronDown size={12} />
            </button>

            {tagDropdownOpen && (
              <div
                className="absolute top-full left-0 mt-1 rounded-lg shadow-lg z-50 min-w-max border"
                style={{
                  backgroundColor: "#1F2937",
                  borderColor: "rgba(229,231,235,0.1)",
                }}
              >
                {tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      onTagChange(tag);
                      setTagDropdownOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 first:rounded-t-lg last:rounded-b-lg"
                  >
                    {tagLabels[tag]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => onDelete(file.id)}
          className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/5 flex-shrink-0"
          title="Remove file"
          aria-label="Remove file"
          disabled={isUpdating}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
